/**
 * Lightweight typeahead / searchable-select. Not headless-ui, no cmdk, just
 * a filtered dropdown attached to an input. Kept simple so it renders in
 * dialogs without portal issues.
 */
import { useEffect, useRef, useState } from "react"
import { Check, ChevronDown, X, Search } from "lucide-react"
import { cn } from "@/lib/utils"

export type ComboOption = {
  value: string
  label: string
  sublabel?: string
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select…",
  disabled,
  clearable = true,
  loading,
  emptyText = "No results",
  className,
  creatable = false,
  createLabel = "Add",
}: {
  options: ComboOption[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  clearable?: boolean
  loading?: boolean
  emptyText?: string
  className?: string
  /**
   * When true, shows an "Add ‘<query>’" option when the search term doesn't
   * match any existing option. Picking it commits the raw query as the value.
   * Useful for free-text-with-suggestions fields like Lead Source.
   */
  creatable?: boolean
  createLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState("")
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = options.find((o) => o.value === value) ?? null
  // When `creatable` is true, the user may have typed in a custom value that
  // doesn't exist in the options list yet (e.g. "Facebook" via the +Add
  // button). Show the raw value in that case so the field doesn't appear
  // empty after selection.
  const displayLabel = selected
    ? selected.label
    : creatable && value && value.trim() !== ""
      ? value
      : null
  const filtered = q
    ? options.filter((o) => {
        const hay = `${o.label} ${o.sublabel ?? ""}`.toLowerCase()
        return hay.includes(q.toLowerCase())
      })
    : options.slice(0, 50)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false)
        setQ("")
      }
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={cn(
          "border-input bg-background text-foreground flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-sm",
          "focus:ring-ring/20 focus:border-ring focus:outline-none focus:ring-4",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
      >
        <span className={cn("truncate text-left", !displayLabel && "text-muted-foreground")}>
          {displayLabel ?? placeholder}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {clearable && displayLabel && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                onChange(null)
              }}
              className="text-muted-foreground hover:text-foreground p-0.5"
              aria-label="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </div>
      </button>

      {open && (
        <div className="border-border absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-white shadow-lg">
          <div className="border-border relative border-b p-2">
            <Search className="text-muted-foreground pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type to search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border-input bg-background h-8 w-full rounded border pl-7 pr-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {loading ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">Loading…</li>
            ) : filtered.length === 0 && !(creatable && q.trim()) ? (
              <li className="px-3 py-4 text-center text-sm text-muted-foreground">{emptyText}</li>
            ) : (
              <>
              {creatable &&
                q.trim() &&
                !options.some(
                  (o) => o.value.toLowerCase() === q.trim().toLowerCase()
                ) && (
                  <li
                    onClick={() => {
                      onChange(q.trim())
                      setOpen(false)
                      setQ("")
                    }}
                    className="hover:bg-rc-blue-50 flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-rc-blue-700 font-medium"
                  >
                    <span className="text-rc-blue-500">+</span>
                    {createLabel} “{q.trim()}”
                  </li>
                )}
              {filtered.map((opt) => {
                const active = opt.value === value
                return (
                  <li
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value)
                      setOpen(false)
                      setQ("")
                    }}
                    className={cn(
                      "hover:bg-rc-grey-50 flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm",
                      active && "bg-rc-blue-50"
                    )}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">{opt.label}</div>
                      {opt.sublabel && (
                        <div className="truncate text-xs text-muted-foreground">{opt.sublabel}</div>
                      )}
                    </div>
                    {active && <Check className="text-rc-blue-600 h-4 w-4 shrink-0" />}
                  </li>
                )
              })}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
